import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertCircle, Check, X } from "lucide-react";

export default function ReservationActionsModal({
  open,
  onClose,
  reservation,
  authAxios,
  onActionComplete,
}) {
  const [actionType, setActionType] = useState(null); // "no-show", "fulfilled", "unblock"
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleAction = async () => {
    if (!reservation || !actionType) return;
    if (actionType === "no-show" && !reason) {
      toast.error("Informe o motivo");
      return;
    }

    setSubmitting(true);
    try {
      if (actionType === "no-show") {
        await authAxios.post(`/admin/reservations/${reservation.id}/mark-no-show`, { reason });
        toast.success("Reserva marcada como no-show");
      } else if (actionType === "fulfilled") {
        await authAxios.post(`/admin/reservations/${reservation.id}/mark-fulfilled`);
        toast.success("Reserva marcada como compareceu");
      } else if (actionType === "unblock") {
        const phone = reservation.customer_phone;
        await authAxios.post(`/admin/customers/${phone}/unblock`, { reason });
        toast.success("Cliente desbloqueado");
      }
      setActionType(null);
      setReason("");
      onActionComplete();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Erro: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!reservation) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o) { setActionType(null); setReason(""); }
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {actionType === null && "Ações da Reserva"}
            {actionType === "no-show" && "Marcar como No-Show"}
            {actionType === "fulfilled" && "Marcar como Compareceu"}
            {actionType === "unblock" && "Desbloquear Cliente"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {actionType === null && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded p-3 text-xs text-gray-600">
                Reserva: <span className="font-semibold">{reservation.customer_name}</span> • {reservation.date} às {reservation.time}
              </div>
              <div className="grid grid-cols-1 gap-2">
                {reservation.status === "confirmed" && (
                  <>
                    <Button variant="outline" onClick={() => setActionType("fulfilled")} className="justify-start">
                      <Check className="w-4 h-4 mr-2 text-emerald-600" />
                      Marcar Presença
                    </Button>
                    <Button variant="outline" onClick={() => setActionType("no-show")} className="justify-start">
                      <X className="w-4 h-4 mr-2 text-red-600" />
                      Marcar No-Show
                    </Button>
                  </>
                )}
                {reservation.status === "no_show" && (
                  <Button variant="outline" onClick={() => setActionType("unblock")} className="justify-start">
                    <Check className="w-4 h-4 mr-2 text-blue-600" />
                    Desbloquear Cliente
                  </Button>
                )}
                {reservation.status === "attended" && (
                  <Button variant="outline" onClick={() => setActionType("no-show")} className="justify-start">
                    <X className="w-4 h-4 mr-2 text-red-600" />
                    Marcar No-Show
                  </Button>
                )}
              </div>
            </div>
          )}
          {actionType === "no-show" && (
            <>
              <div className="bg-red-50 border border-red-200 rounded p-3 flex gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-semibold mb-1">Confirmação de No-Show</p>
                  <p className="text-xs">Será criado evento imutável. Cliente poderá ser bloqueado por até 6 meses.</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Motivo (obrigatório)</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: cliente não compareceu, não atendeu chamada"
                  rows={2}
                />
              </div>
            </>
          )}

          {actionType === "fulfilled" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex gap-2">
              <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-700">
                <p className="font-semibold mb-1">Confirmar Presença</p>
                <p className="text-xs">Se o cliente estava em "aviso", será removido. Histórico de faltas preservado.</p>
              </div>
            </div>
          )}

          {actionType === "unblock" && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 flex gap-2">
              <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-semibold mb-1">Desbloquear Cliente</p>
                <p className="text-xs">Cliente poderá fazer novas reservas imediatamente. Histórico de faltas será preservado.</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {actionType === null ? (
            <Button variant="outline" onClick={onClose}>Fechar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setActionType(null)}>Voltar</Button>
              <Button onClick={handleAction} disabled={submitting} className="text-white bg-red-600 hover:bg-red-700">
                {submitting ? "Processando..." : "Confirmar"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
